/**
 * The relay band of the map's site detail panel — the same relay cards the site
 * dashboard shows, stacked down the panel's single column beneath the alarm and
 * maintenance summaries so the whole site reads in one scroll.
 *
 * Contains:
 * - SiteDetailRelays — the panel's stack of relay cards (default export)
 */
import { Box, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import ScopeContext from '@sophocles/app/ScopeContext';
import { selectCapabilities } from '@sophocles/features/capabilities';
import { selectDefaultLoadTypeIdForCustomerType, selectRelayLoadTypes } from '@sophocles/features/reference';
import { resolveScopeCustomerTypeId } from '@sophocles/hooks/relayLoadTerm';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import CompactRelayCard from '@sophocles/views/sites/components/RelayCards/CompactRelayCard';
import RelayCard from '@sophocles/views/sites/components/RelayCards/RelayCard';
import {
  deriveRelayCardLayout,
  resolveRelayLoadType,
} from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';

// Unused relays share one block, in the same 2-up pack the dashboard groups
// them into — four empty relays are worth one card's worth of attention.
const SETUP_GROUP_COLUMNS = 2;

/**
 * @param {object} props
 * @param {boolean} props.loading - the site snapshot is still refreshing.
 * @param {string|number} props.siteId - the open site.
 * @param {?object} props.unit - the site's unit snapshot.
 * @param {Array} props.relays - the unit's relays, already sorted.
 * @param {string} props.siteTimeZone - the site's IANA zone.
 * @param {object} props.customer - the legacy customer entity RelayCard reads options from.
 * @param {object} props.params - `{cust_id, sitegroup_id, site_id}` for the calendar jump.
 * @param {boolean} props.usesCalendar - the site's CALENDAR feature is on.
 * @param {boolean} props.isBillboard - the site is a billboard structure.
 * @param {boolean} props.hasCharting - the customer has charting.
 * @param {?object} props.curfew - the group's active curfew window.
 * @param {?string} props.siteIccid - the radio's ICCID.
 * @param {?string} props.nextSunriseRaw - next sunrise, as the snapshot reports it.
 * @param {?string} props.nextSunsetRaw - next sunset, as the snapshot reports it.
 * @param {Function} props.onClickEditSchedule - open the relay blade (relayId, scheduleId, tab).
 * @param {boolean} props.canManageUnit - the viewer may change this unit.
 */
export default function SiteDetailRelays({
  loading,
  siteId,
  unit,
  relays,
  siteTimeZone,
  customer,
  params,
  usesCalendar,
  isBillboard,
  hasCharting,
  curfew,
  siteIccid,
  nextSunriseRaw,
  nextSunsetRaw,
  onClickEditSchedule,
  canManageUnit,
}) {
  const { user } = useUser();
  const capabilities = useSelector(selectCapabilities);

  const scope = useContext(ScopeContext);
  const customerTypeId = resolveScopeCustomerTypeId(scope);
  const loadTypes = useSelector(selectRelayLoadTypes);
  const defaultLoadTypeId = useSelector(selectDefaultLoadTypeIdForCustomerType(customerTypeId));
  const loadTypeById = useMemo(() => new Map(loadTypes.map(loadType => [loadType.id, loadType])), [loadTypes]);
  const relayById = useMemo(() => new Map(relays.map(relay => [relay.id, relay])), [relays]);

  const { configuredRelays, unconfiguredRelayGroups } = useMemo(() => deriveRelayCardLayout(relays), [relays]);

  // A primary relay names its partner by id; RelayCard reads it as an object,
  // and a relay with no load type of its own renders as the customer type's
  // default. Same resolution the dashboard's relay section does.
  const resolveRelay = relay => {
    const secondaryRelay = relay.secondary_relay_id != null ? (relayById.get(relay.secondary_relay_id) ?? null) : null;
    return {
      ...relay,
      load_type: resolveRelayLoadType(relay, loadTypeById, defaultLoadTypeId),
      secondary_relay: secondaryRelay
        ? {
            ...secondaryRelay,
            load_type: resolveRelayLoadType(secondaryRelay, loadTypeById, defaultLoadTypeId),
          }
        : null,
    };
  };

  const canSetUpRelay = userPermCheck(user, 'pliny.add_relayload');
  const canSendRelayCommands = capabilities.can_send_command ?? false;

  if (configuredRelays.length === 0 && unconfiguredRelayGroups.length === 0) return null;

  return (
    <Stack spacing={2}>
      {configuredRelays.map(relay => (
        <RelayCard
          key={relay.id}
          loading={loading}
          siteId={siteId}
          unit={unit}
          siteTimeZone={siteTimeZone}
          customer={customer}
          params={params}
          usesCalendar={usesCalendar}
          relay={resolveRelay(relay)}
          relays={relays}
          isBillboard={isBillboard}
          hasCharting={hasCharting}
          curfew={curfew}
          siteIccid={siteIccid}
          nextSunriseRaw={nextSunriseRaw}
          nextSunsetRaw={nextSunsetRaw}
          onClickEditSchedule={onClickEditSchedule}
          hideOptions={!canManageUnit}
        />
      ))}

      {unconfiguredRelayGroups.map(relayGroup => (
        <Box
          key={`unconfigured-relays-${relayGroup[0].id}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(relayGroup.length, SETUP_GROUP_COLUMNS)}, minmax(0, 1fr))`,
            gap: 2,
          }}
        >
          {relayGroup.map(relay => (
            <CompactRelayCard
              key={relay.id}
              relay={resolveRelay(relay)}
              siteId={siteId}
              canSetUp={canSetUpRelay}
              canSendCommands={canSendRelayCommands}
              onSetup={() => onClickEditSchedule(relay.id, null, 'loads')}
            />
          ))}
        </Box>
      ))}
    </Stack>
  );
}

SiteDetailRelays.propTypes = {
  loading: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.object,
  relays: PropTypes.array,
  siteTimeZone: PropTypes.string,
  customer: PropTypes.object,
  params: PropTypes.object,
  usesCalendar: PropTypes.bool,
  isBillboard: PropTypes.bool,
  hasCharting: PropTypes.bool,
  curfew: PropTypes.object,
  siteIccid: PropTypes.string,
  nextSunriseRaw: PropTypes.string,
  nextSunsetRaw: PropTypes.string,
  onClickEditSchedule: PropTypes.func.isRequired,
  canManageUnit: PropTypes.bool,
};
